import type { File } from '@assembly-web/services';
import {
  FileType,
  getFileColorForFileCard,
  getFileTypeForFile,
} from '@assembly-web/services';
import { ArrowDownTrayIcon, LinkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { twJoin } from 'tailwind-merge';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import type { ToolbarItem } from '../../../DesignSystem/Feedback/Toolbar';
import { Toolbar } from '../../../DesignSystem/Feedback/Toolbar';
import { classNames } from '../../../DesignSystem/Utils/classNames';
import { imageIcon, WarningIcon } from '../../assets/icons';
import { getFileIconForFileCard } from './utils';

const secondaryToolbarItems = [
  {
    id: 'copyLink',
    icon: LinkIcon,
    text: 'Copy link',
  },
  {
    id: 'downloadFile',
    icon: ArrowDownTrayIcon,
    text: 'Download file',
  },
];

const messages = defineMessages({
  clickToViewFile: {
    defaultMessage: 'Click to view file',
    id: 'j3vBX5',
  },
});

function ImageCard({
  file,
  onFileClick,
  numFiles,
}: {
  file: File;
  onFileClick: (fileName: string) => void;
  numFiles: number;
}) {
  const [error, setError] = useState(false);
  if (error) {
    return (
      <div className="w-24 rounded-lg border border-gray-5">
        <TextStyle
          variant="xs-regular"
          className="truncate border-b border-gray-5 p-2"
        >
          {file.name}
        </TextStyle>
        <img
          src={imageIcon}
          className="mx-auto my-0 box-content h-6 w-6 pb-5 pt-4"
          alt=""
        />
      </div>
    );
  }
  return (
    <button onClick={() => onFileClick(file.name)}>
      <img
        className={classNames(
          'rounded hover:scale-[1.015] hover:cursor-zoom-in',
          numFiles === 1 && 'object-cover',
          numFiles > 1 && 'w-[160px] object-cover max-md:w-[140px]'
        )}
        src={file.thumbnails[360]}
        alt={file.name}
        onError={() => setError(true)}
      />
    </button>
  );
}

function ImageCards({
  files,
  onFileClick,
}: {
  files: File[];
  onFileClick: (fileName: string) => void;
}) {
  return (
    <div className={twJoin('', files.length > 1 && 'grid grid-cols-2 gap-2')}>
      {files.map((file) => (
        <ImageCard
          key={file._id}
          file={file}
          onFileClick={onFileClick}
          numFiles={files.length}
        />
      ))}
      <div className="absolute bottom-10 left-0 flex h-10 w-full items-center justify-center bg-gradient-to-t from-[white] to-[transparent]"></div>
    </div>
  );
}

const NonImageCard = ({
  file,
  isLast,
  onFileClick,
  onToolbarItemClick,
  showInCarousel,
}: {
  file: File;
  isLast: boolean;
  onFileClick: (fileName: string) => void;
  onToolbarItemClick?: ({
    args,
    fileName,
  }: {
    args: ToolbarItem;
    fileName: string;
  }) => void;
  showInCarousel?: boolean;
}) => {
  const uploadFailed = file.size === 0;
  const bgColor = uploadFailed ? 'error-2' : getFileColorForFileCard(file.type);
  const logo = getFileIconForFileCard(file.type);

  const { formatMessage } = useIntl();

  return (
    <div className="relative flex">
      <button
        className={classNames('group flex h-[80px] w-full', {
          'hover:scale-[1.015] hover:cursor-zoom-in': !uploadFailed,
          'mb-2': !isLast || !showInCarousel,
          'my-1': showInCarousel,
        })}
        onClick={() => onFileClick(file.name)}
        disabled={uploadFailed}
      >
        <div
          className={classNames(
            'flex h-[80px] w-[80px] items-center justify-center rounded-l border-r-[1px] border-gray-4',
            `bg-${bgColor}`
          )}
        >
          {uploadFailed ? (
            <WarningIcon className="h-8 w-8 text-error-6" />
          ) : (
            <img
              className="mx-auto my-auto h-[24px] w-[24px]"
              src={logo}
              alt=""
            />
          )}
        </div>
        <div className="relative flex h-[80px] flex-1 flex-col items-start justify-center overflow-hidden rounded-r border-y-[1px] border-r-[1px] border-gray-4 px-3 py-2">
          <p className="h-[22px] overflow-hidden text-ellipsis break-normal font-medium">
            {file.originalName ?? file.name}
          </p>
          <p className="text-xs text-gray-8">
            {formatMessage(messages.clickToViewFile)}
          </p>
        </div>
      </button>
      {onToolbarItemClick && (
        <div className="absolute right-2 top-2">
          <Toolbar
            hideToolbar={false}
            onMenuItemClick={(args) =>
              onToolbarItemClick({ args, fileName: file.name })
            }
            secondaryToolbarItems={secondaryToolbarItems}
          />
        </div>
      )}
    </div>
  );
};

function NonImageCards({
  files,
  onFileClick,
  onToolbarItemClick,
  showInCarousel,
}: {
  files: File[];
  onFileClick: (fileName: string) => void;
  onToolbarItemClick?: ({
    args,
    fileName,
  }: {
    args: ToolbarItem;
    fileName: string;
  }) => void;
  showInCarousel?: boolean;
}) {
  return (
    <div className="max-w-[500px] text-sm text-gray-9">
      {files.map((file, index) => {
        return (
          <NonImageCard
            key={file._id}
            file={file}
            isLast={index === files.length - 1}
            onFileClick={onFileClick}
            onToolbarItemClick={onToolbarItemClick}
            showInCarousel={showInCarousel}
          />
        );
      })}
    </div>
  );
}

export function FileBlock({
  files,
  onFileClick,
  onToolbarItemClick,
  showInCarousel,
}: {
  files: File[];
  onFileClick: (fileName: string) => void;
  onToolbarItemClick?: ({
    args,
    fileName,
  }: {
    args: ToolbarItem;
    fileName: string;
  }) => void;
  showInCarousel?: boolean;
}) {
  const imageFiles = files.filter(
    (file) => getFileTypeForFile(file.type) === FileType.Image
  );
  const otherFiles = files.filter(
    (file) => getFileTypeForFile(file.type) !== FileType.Image
  );

  return (
    <div className="overflow-hidden">
      {imageFiles.length > 0 && (
        <ImageCards files={imageFiles} onFileClick={onFileClick} />
      )}
      {otherFiles.length > 0 && (
        <NonImageCards
          files={otherFiles}
          onFileClick={onFileClick}
          onToolbarItemClick={onToolbarItemClick}
          showInCarousel={showInCarousel}
        />
      )}
    </div>
  );
}
